import {
  DEFAULT_NATIVE_LANGUAGE,
  DEFAULT_TARGET_LANGUAGE,
} from '@/features/common/constants/Constants';
import {
  getCurrentDayIndex,
  getCurrentWeekNumber,
} from '@/features/learn/utilities/dateUtilities';
import { Achievement, ProfileTypes } from '@/interfaces/ProfileTypes';
import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import { ACHIEVEMENTS_LIST } from '@/features/profile/constants';

export interface LanguageProgressItem {
  bananas: number;
  progress: {
    [unitId: string]: {
      [lessonId: string]: number;
    };
  };
  goal: {
    weekNumber: number;
    userDailyGoal: number;
    weeklyGraphData: number[];
  };
}

interface LanguageProgress {
  [language: string]: LanguageProgressItem;
}

export interface UserState {
  profile: {
    name: string;
    email: string;
    photoURL: string;
    uid: string;
    targetLanguage: string;
    nativeLanguage: string;
    bananas: number;
    currentStreak: number;
    longestStreak: number;
    streakSaver: {
      earnedStreakSaver: number;
      appliedDates: string[];
      earnedDates: string[];
      realConsecutiveDays: number;
    };
    streakChallenge?: {
      isAccepted: boolean;
      isCompleted: boolean;
      completedDate: number | null;
      counter: number;
      isLostChallenge: boolean;
    };
    streakRecords?: string[];
    dateLastPlayed?: number;
    web2waveUUID?: string;
    achievements?: Achievement[];
    streaks?: {
      currentStreak: number;
      longestStreak: number;
      currentStreakStartDate: string | null;
      dateLastPlayed: number | null;
      streakRecords?: string[];
      streakSaver: {
        earnedStreakSaver: number;
        realConsecutiveDays: number;
        appliedDates?: string[];
        earnedDates?: string[];
      };
      streakChallenge: {
        isAccepted: boolean;
        isCompleted: boolean;
        completedDate: number | null;
        counter: number;
        isLostChallenge: boolean;
      };
    };
  };
  languageProgress: LanguageProgress;
  settings: {
    isEffectEnabled: boolean;
    isReminderOn: boolean;
    isTranscriptEnabled: boolean;
    isWeeklyProgressOn: boolean;
    voiceSpeed: number;
    isSoundAutoPlayOn: boolean;
    isKeyboardShortcutEnabled: boolean;
  };
  // Flag to control streak screen display
  shouldShowStreakScreen: boolean;
}

const initialState: UserState = {
  profile: {
    name: '',
    email: '',
    photoURL: '',
    uid: '',
    targetLanguage: DEFAULT_TARGET_LANGUAGE,
    nativeLanguage: DEFAULT_NATIVE_LANGUAGE,
    bananas: 0,
    currentStreak: 0,
    longestStreak: 0,
    streakSaver: {
      earnedStreakSaver: 1,
      appliedDates: [] as string[],
      earnedDates: [] as string[],
      realConsecutiveDays: 7,
    },
    streakRecords: [],
    dateLastPlayed: -1,
    web2waveUUID: undefined,
    achievements: ACHIEVEMENTS_LIST,
  },
  languageProgress: {},
  settings: {
    isEffectEnabled: true,
    isReminderOn: true,
    isTranscriptEnabled: true,
    isWeeklyProgressOn: true,
    voiceSpeed: 1,
    isSoundAutoPlayOn: true,
    isKeyboardShortcutEnabled: true,
  },
  shouldShowStreakScreen: false,
};

export const userSlice = createSlice({
  name: 'userSlice',
  initialState,
  reducers: {
    setProfile: (state, action: PayloadAction<ProfileTypes>) => {
      state.profile = { ...state.profile, ...action.payload };
    },

    setSingleLanguageProgress: (
      state,
      action: PayloadAction<{
        targetLanguage: string;
        progress: LanguageProgressItem;
      }>,
    ) => {
      const { targetLanguage, progress } = action.payload;
      if (!state.languageProgress) {
        state.languageProgress = {};
      }
      state.languageProgress[targetLanguage] = progress;
    },

    setTargetLanguage: (state, action: PayloadAction<string>) => {
      const newTargetLanguage = action.payload;
      state.profile.targetLanguage = newTargetLanguage;

      // Initialize progress structure for new language if it doesn't exist
      if (!state.languageProgress[newTargetLanguage]) {
        state.languageProgress[newTargetLanguage] = {
          bananas: 0,

          progress: {},
          goal: {
            weekNumber: getCurrentWeekNumber(),
            userDailyGoal: 0,
            weeklyGraphData: [0, 0, 0, 0, 0, 0, 0],
          },
        };
      }
    },

    setNativeLanguage: (state, action: PayloadAction<string>) => {
      state.profile.nativeLanguage = action.payload;
    },

    updateLanguageBananas: (
      state,
      action: PayloadAction<{
        targetLanguage: string;
        bananas: number;
      }>,
    ) => {
      const { targetLanguage, bananas } = action.payload;

      // Initialize language progress if it doesn't exist
      if (!state.languageProgress[targetLanguage]) {
        state.languageProgress[targetLanguage] = {
          bananas: 0,
          progress: {},
          goal: {
            weekNumber: getCurrentWeekNumber(),
            userDailyGoal: 0,
            weeklyGraphData: [0, 0, 0, 0, 0, 0, 0],
          },
        };
      }
      state.languageProgress[targetLanguage].bananas = bananas;
    },

    updateLessonCrowns: (
      state,
      action: PayloadAction<{
        targetLanguage: string;
        unitId: string;
        lessonId: number;
        crowns: number;
      }>,
    ) => {
      const { targetLanguage, unitId, lessonId, crowns } = action.payload;

      // Initialize necessary structures if they don't exist
      if (!state.languageProgress[targetLanguage]) {
        state.languageProgress[targetLanguage] = {
          bananas: 0,
          progress: {},
          goal: {
            weekNumber: getCurrentWeekNumber(),
            userDailyGoal: 0,
            weeklyGraphData: [0, 0, 0, 0, 0, 0, 0],
          },
        };
      }
      if (!state.languageProgress[targetLanguage].progress[unitId]) {
        state.languageProgress[targetLanguage].progress[unitId] = {};
      }
      state.languageProgress[targetLanguage].progress[unitId][lessonId] =
        crowns;
    },

    updateUserSettings: (
      state,
      action: PayloadAction<{
        settings: Partial<UserState['settings']>;
      }>,
    ) => {
      const { settings } = action.payload;
      if (settings) {
        state.settings = {
          ...state.settings,
          ...settings,
        };
      }
    },

    setUserProfileAndSettings: (
      state,
      action: PayloadAction<{
        profile: Partial<UserState['profile']>;
        settings?: Partial<UserState['settings']>;
      }>,
    ) => {
      const { profile, settings } = action.payload;
      if (profile) {
        state.profile = {
          ...state.profile,
          ...profile,
        };
      }
      if (settings) {
        state.settings = {
          ...state.settings,
          ...settings,
        };
      }
    },

    updateBananaPoints: (
      state,
      action: PayloadAction<{
        targetLanguage: string;
        points: number;
      }>,
    ) => {
      const { targetLanguage, points } = action.payload;

      if (!state.languageProgress[targetLanguage]) {
        state.languageProgress[targetLanguage] = {
          bananas: 0,
          progress: {},
          goal: {
            weekNumber: getCurrentWeekNumber(),
            userDailyGoal: 0,
            weeklyGraphData: [0, 0, 0, 0, 0, 0, 0],
          },
        };
      }

      state.languageProgress[targetLanguage].bananas += points;

      if (!state.languageProgress[targetLanguage].goal?.weeklyGraphData) {
        state.languageProgress[targetLanguage].goal.weeklyGraphData = [
          0, 0, 0, 0, 0, 0, 0,
        ];
      }

      const currentWeekNumber = getCurrentWeekNumber();
      const storedWeekNumber =
        state.languageProgress[targetLanguage].goal.weekNumber || 0;

      if (currentWeekNumber !== storedWeekNumber) {
        state.languageProgress[targetLanguage].goal.weeklyGraphData = [
          0, 0, 0, 0, 0, 0, 0,
        ];
        state.languageProgress[targetLanguage].goal.weekNumber =
          currentWeekNumber;
      }

      const dayIndex = getCurrentDayIndex();
      const weeklyData =
        state.languageProgress[targetLanguage].goal.weeklyGraphData;
      weeklyData[dayIndex] += points;
    },

    checkWeeklyDataReset: state => {
      const targetLanguage = state.profile.targetLanguage;
      if (!targetLanguage || !state.languageProgress[targetLanguage]) return;

      const currentWeekNumber = getCurrentWeekNumber();
      const storedWeekNumber =
        state.languageProgress[targetLanguage].goal.weekNumber || 0;

      if (currentWeekNumber !== storedWeekNumber) {
        state.languageProgress[targetLanguage].goal.weeklyGraphData = [
          0, 0, 0, 0, 0, 0, 0,
        ];
        state.languageProgress[targetLanguage].goal.weekNumber =
          currentWeekNumber;
      }
    },

    updateAchievements: (state, action: PayloadAction<any[]>) => {
      state.profile.achievements = action.payload;
    },

    setShouldShowStreakScreen: (state, action: PayloadAction<boolean>) => {
      state.shouldShowStreakScreen = action.payload;
    },
  },
});

export const {
  setProfile,
  setUserProfileAndSettings,
  updateUserSettings,
  setTargetLanguage,
  setNativeLanguage,
  updateLanguageBananas,
  updateLessonCrowns,
  updateBananaPoints,
  setSingleLanguageProgress,
  checkWeeklyDataReset,
  updateAchievements,
  setShouldShowStreakScreen,
} = userSlice.actions;

export default userSlice.reducer;
